import React, {createContext, useContext} from "react";

// The IANA zone report timestamps render in, or undefined for the viewer's own zone.
// Components outside a provider (e.g. SpaCommandsTable on the commands page) keep the
// browser's local time.
const ReportTimeZoneContext = createContext<string | undefined>(undefined);

export const ReportTimeZoneProvider: React.FC<{timeZone?: string, children: React.ReactNode}> = ({timeZone, children}) =>
  <ReportTimeZoneContext.Provider value={timeZone}>{children}</ReportTimeZoneContext.Provider>;

// Firmware-reported zones aren't guaranteed to be valid IANA names; Intl throws a
// RangeError on an unknown one, which would blank the whole report.
export const isValidTimeZone = (tz: unknown): tz is string => {
  if (typeof tz !== "string" || tz === "") return false;
  try {
    new Intl.DateTimeFormat(undefined, {timeZone: tz});
    return true;
  } catch {
    return false;
  }
};

export const formatInZone = (value: string | number | Date, timeZone?: string) =>
  new Date(value).toLocaleString(undefined, {timeZone});

// Short zone label for the given instant, e.g. "MDT" or "GMT+2".
export const zoneAbbr = (timeZone?: string, at: Date = new Date()) =>
  new Intl.DateTimeFormat("en-US", {timeZone, timeZoneName: "short"})
    .formatToParts(at)
    .find(p => p.type === "timeZoneName")?.value ?? timeZone ?? "";

export const useReportTimeFormat = () => {
  const timeZone = useContext(ReportTimeZoneContext);
  return (value: string | number | Date) => formatInZone(value, timeZone);
};
